import {makeAutoObservable, runInAction} from "mobx";
import {IOption} from "../components/inputs/select-dropdown/select-dropdown";
import {ISetting} from "../models/User";
import {rootContainer, TYPE} from "../services/container/root";
import TimezoneService, {DATE_FORMAT, TIME_FORMAT} from "../services/timezone-service";
import {IChangeable} from "./types";

interface ITimezoneSettings {
    timezone: string;
    date_format: DATE_FORMAT;
    time_format: TIME_FORMAT;
}

/**
 * TimezoneStore
 *
 * Stores user's timezone preferences (timezone, date format, time format).
 * These settings are used by:
 * - DateTimeV2 component (via useDateTimeFormattingParams hook)
 * - DateTime component (legacy)
 * - DateRangePickerV2 component
 * - getFormattedDate utility function
 *
 * @implements {IChangeable<ITimezoneSettings>}
 */
class TimezoneStore implements IChangeable<ITimezoneSettings>{
    private _options: IOption[] = [];
    private _isLoadingTimezoneList: boolean = false;
    private _model: ITimezoneSettings;
    private _changes: Partial<ITimezoneSettings> = {};
    private _wasTimezoneListLoaded: boolean = false;
    
    constructor(userSettings: ISetting){
        this._model = ensureValidModel(userSettings);
        makeAutoObservable(this, {}, {autoBind: true});
    }
        
    public get timezoneOptions(){ return this._options; }
    public get isLoadingTimezoneList(){ return this._isLoadingTimezoneList; }

    /** User's preferred IANA timezone name (e.g. 'America/New_York', 'Europe/Kiev', 'UTC') */
    public get timezone(): string { return this._changes.timezone ?? this._model.timezone; }

    /** User's preferred date format (e.g. dd/MM/yyyy, MM/dd/yyyy) */
    public get dateFormat(): DATE_FORMAT { return this._changes.date_format ?? this._model.date_format; }

    /** User's preferred time format (12h AM/PM or 24h) */
    public get timeFormat(): TIME_FORMAT { return this._changes.time_format ?? this._model.time_format; }

    private get _service(): TimezoneService { return rootContainer.get(TYPE.TimezoneService); }
    
    /**
     * Loads list of available IANA timezone options for dropdown.
     * Only loads once per store instance.
     */
    public async loadTimezoneList(){
        if(this._wasTimezoneListLoaded || this._isLoadingTimezoneList){
            return;
        }
        
        this._isLoadingTimezoneList = true;
        try {
            const res = await this._service.loadTimezoneList();
            runInAction(() => {
                this._options = res.list;
                this._isLoadingTimezoneList = false;
                this._wasTimezoneListLoaded = true;
            });
        }
        catch (e) {
            runInAction(() => {
                this._isLoadingTimezoneList = false;
            });
        }
    }
    
    public hasChanges(): boolean {
        return Object.keys(this._changes).length > 0;
    }
    public discardChanges(): void {
        this._changes = {};
    }
    
    public getUpdatedModel(): ITimezoneSettings  {
        return  {...this._model!, ...this._changes} ;
    }
    
    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof ITimezoneSettings;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }
    
    /**
     * Updates timezone preference.
     * Changes are tracked separately and can be discarded or saved.
     * @param value - IANA timezone name (e.g. 'America/New_York')
     */
    public setTimezone(value: string) {
        this._changes.timezone = value;
        this._clearUnneededChanges();
    }
    
    /**
     * Updates date format preference.
     * @param value - DATE_FORMAT enum value
     */
    public setDateFormat(value: DATE_FORMAT) {
        this._changes.date_format = value;
        this._clearUnneededChanges();
    }
    
    /**
     * Updates time format preference.
     * @param value - TIME_FORMAT enum value (12h or 24h)
     */
    public setTimeFormat(value: TIME_FORMAT) {
        this._changes.time_format = value;
        this._clearUnneededChanges();
    }
    
}
function ensureValidModel(userSettings: ISetting): ITimezoneSettings {
    return {
        timezone: userSettings.timezone,
        date_format: userSettings.date_format,
        time_format: userSettings.time_format
    };
}
export default TimezoneStore;
